import api from './api';

// ─── Empty initial shape ──────────────────────────────────────────────────────
export const EMPTY_CONFIG = {
  departments: [], locations: [], classifications: [], designations: [],
  employmentTypes: [], payGrades: [], salaryStructures: [], allowanceTypes: [],
  leaveTypes: [], publicHolidays: [], maternityLeavePolicies: [],
  unpaidInfantCareLeaveRelevantPeriods: [], cpfRates: [], cpfPrYearRates: [],
  cpfWageCeilings: [], shgContributionTiers: [], irasTaxBrackets: [],
  irasTaxReliefs: [], workweekPolicies: [], workScheduleTemplates: [],
  overtimePolicies: [], noticePolicies: [], retirementPolicies: [],
  foreignWorkerLevies: [], wicaCompensations: [], systemSettings: [],
  integrationKeys: [], customFields: [], approvalRules: [],
  notificationTemplates: [], auditConfigs: [], safetyCertifications: [],
  tradeCategories: [], competencyLevels: [], skillFrameworks: [],
  benefitPlans: [], insurancePolicies: [], medicalExams: [],
  competencyFrameworks: [], cpdRequirements: [], workPassQuotas: [],
  spassSalaryRequirements: [], workPermitRequirements: [],
  constructionWorkerUpgradePathways: [], certificationRequirements: [],
  constructionWorkerClassifications: [], workPermitLevyCalculations: [],
  myskillsfutureCourses: [], courseDisciplines: [], trainingProviders: [],
  employeeTrainingEnrollments: [], trainingRecommendations: [],
  departmentTrainingBudgets: [], trainingApplications: [],
  sponsorshipBondAgreements: [], trainingApprovalLogs: [],
  trainingBookingTasks: [], sponsorshipRepayments: [],
  uenVerification: [], institutionTypes: [], educationInstitutions: [],
  qualificationLevels: [], fieldsOfStudy: [], certificationAuthorities: [],
  certificationCategories: [], certificationTemplates: [],
  momWorkingDaysCalendar: [],
};

// ─── camelCase → kebab-case API endpoint map ─────────────────────────────────
const ENDPOINT_MAP = {
  departments: 'departments', locations: 'locations',
  classifications: 'classifications', designations: 'designations',
  employmentTypes: 'employment-types', payGrades: 'pay-grades',
  salaryStructures: 'salary-structures', allowanceTypes: 'allowance-types',
  leaveTypes: 'leave-types', publicHolidays: 'public-holidays',
  cpfRates: 'cpf-contribution-rates', workweekPolicies: 'workweek-policies',
  workScheduleTemplates: 'work-schedule-templates',
  overtimePolicies: 'overtime-policies',
  noticePolicies: 'notice-period-policies',
  retirementPolicies: 'retirement-policies',
  foreignWorkerLevies: 'foreign-worker-levies',
  wicaCompensations: 'wica-compensations',
  systemSettings: 'system-settings', integrationKeys: 'integration-keys',
  customFields: 'custom-fields', approvalRules: 'approval-rules',
  notificationTemplates: 'notification-templates',
  auditConfigs: 'audit-logging-configs',
  safetyCertifications: 'safety-certification-types',
  tradeCategories: 'trade-categories', competencyLevels: 'competency-levels',
  skillFrameworks: 'skill-frameworks', benefitPlans: 'benefit-plans',
  insurancePolicies: 'insurance-policy-types', medicalExams: 'medical-exam-types',
  competencyFrameworks: 'competency-frameworks', cpdRequirements: 'cpd-requirements',
  workPassQuotas: 'work-pass-quotas',
  institutionTypes: 'institution-types',
  educationInstitutions: 'education-institutions',
  qualificationLevels: 'qualification-levels',
  fieldsOfStudy: 'fields-of-study',
  certificationAuthorities: 'certification-authorities',
  certificationCategories: 'certification-categories',
  certificationTemplates: 'certification-templates',
  momWorkingDaysCalendar: 'mom-working-days-calendars',
  maternityLeavePolicies: 'maternity-leave-policies',
  unpaidInfantCareLeaveRelevantPeriods: 'unpaid-infant-care-leave-relevant-periods',
  cpfPrYearRates: 'cpf-pr-year-rates', cpfWageCeilings: 'cpf-wage-ceilings',
  shgContributionTiers: 'shg-contribution-tiers',
  irasTaxBrackets: 'iras-tax-brackets',
  irasTaxReliefs: 'iras-tax-reliefs',
  sdlPolicies: 'sdl-policies',
  spassSalaryRequirements: 'spass-salary-requirements',
  workPermitRequirements: 'work-permit-requirements',
  constructionWorkerUpgradePathways: 'construction-worker-upgrade-pathways',
  certificationRequirements: 'certification-requirements',
  constructionWorkerClassifications: 'construction-worker-classifications',
  workPermitLevyCalculations: 'work-permit-levy-calculations',
  myskillsfutureCourses: 'myskillsfuture-courses',
  courseDisciplines: 'course-disciplines', trainingProviders: 'training-providers',
  employeeTrainingEnrollments: 'employee-training-enrollments',
  trainingRecommendations: 'training-recommendations',
  departmentTrainingBudgets: 'department-training-budgets',
  trainingApplications: 'training-applications',
  sponsorshipBondAgreements: 'sponsorship-bond-agreements',
  trainingApprovalLogs: 'training-approval-logs',
  trainingBookingTasks: 'training-booking-tasks',
  sponsorshipRepayments: 'sponsorship-repayments',
  uenVerification: 'uen-verification',
  applicationRules: 'application-rules',
};

// ─── Human-readable category titles ──────────────────────────────────────────
const TITLE_MAP = {
  departments: 'Department', locations: 'Location',
  classifications: 'Classification', designations: 'Designation',
  employmentTypes: 'Employment Type', payGrades: 'Pay Grade',
  salaryStructures: 'Salary Structure', allowanceTypes: 'Allowance Type',
  leaveTypes: 'Leave Type', publicHolidays: 'Public Holiday',
  cpfRates: 'CPF Rate', workweekPolicies: 'Workweek Policy',
  workScheduleTemplates: 'Work Schedule Template',
  overtimePolicies: 'Overtime Policy', noticePolicies: 'Notice Policy',
  retirementPolicies: 'Retirement Policy',
  foreignWorkerLevies: 'Foreign Worker Levy',
  wicaCompensations: 'WICA Compensation', systemSettings: 'System Setting',
  integrationKeys: 'Integration Key', customFields: 'Custom Field',
  approvalRules: 'Approval Rule', notificationTemplates: 'Notification Template',
  auditConfigs: 'Audit Config',
  institutionTypes: 'Institution Type',
  educationInstitutions: 'Education Institution',
  qualificationLevels: 'Qualification Level',
  fieldsOfStudy: 'Field of Study',
  certificationAuthorities: 'Certification Authority',
  certificationCategories: 'Certification Category',
  certificationTemplates: 'Certification Template',
  momWorkingDaysCalendar: 'MOM Working Days Calendar',
  maternityLeavePolicies: 'Maternity Leave Policy',
  unpaidInfantCareLeaveRelevantPeriods: 'Unpaid Infant Care Leave Relevant Period',
  cpfPrYearRates: 'CPF PR Year Rate', cpfWageCeilings: 'CPF Wage Ceiling',
  shgContributionTiers: 'SHG Contribution Tier',
  spassSalaryRequirements: 'S Pass Salary Requirement',
  workPermitRequirements: 'Work Permit Requirement',
  constructionWorkerUpgradePathways: 'Construction Worker Upgrade Pathway',
  certificationRequirements: 'Certification Requirement',
  constructionWorkerClassifications: 'Construction Worker Classification',
  workPermitLevyCalculations: 'Work Permit Levy Calculation',
  myskillsfutureCourses: 'MySkillsFuture Course',
  courseDisciplines: 'Course Discipline', trainingProviders: 'Training Provider',
  employeeTrainingEnrollments: 'Employee Training Enrollment',
  trainingRecommendations: 'Training Recommendation',
  departmentTrainingBudgets: 'Department Training Budget',
  trainingApplications: 'Training Application',
  sponsorshipBondAgreements: 'Sponsorship Bond Agreement',
  trainingApprovalLogs: 'Training Approval Log',
  trainingBookingTasks: 'Training Booking Task',
  sponsorshipRepayments: 'Sponsorship Repayment',
  uenVerification: 'UEN Verification', applicationRules: 'Application Rule',
};

export const getCategoryEndpoint = (category) => ENDPOINT_MAP[category] || category;
export const getCategoryTitle = (category) => TITLE_MAP[category] || 'Configuration';

// ─── Paginated fetcher ────────────────────────────────────────────────────────
const fetchAllPages = async (endpoint) => {
  const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  if (!token) throw new Error('Authentication required. Please log in again.');

  let allResults = [];
  let currentPage = 1;
  let hasMore = true;

  while (hasMore) {
    try {
      const url = `${endpoint}${endpoint.includes('?') ? '&' : '?'}page=${currentPage}&page_size=100`;
      const response = await api.get(url);
      const results = response.data.results || response.data || [];
      const totalCount = response.data.count;

      allResults = allResults.concat(results);

      const hasMoreFromCount = totalCount && currentPage * 100 < totalCount;
      const hasMoreFromNext = !!response.data.next;
      hasMore = hasMoreFromCount || hasMoreFromNext;
      currentPage++;

      if (currentPage > 50) break;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      hasMore = false;
    }
  }

  return allResults;
};

// ─── Bulk loader (used by React Query) ───────────────────────────────────────
export const loadAllConfigData = async () => {
  const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  if (!token) throw new Error('Please log in to access system settings.');

  const endpoints = [
    '/configuration/departments', '/configuration/locations',
    '/configuration/classifications', '/configuration/designations',
    '/configuration/employment-types', '/configuration/pay-grades',
    '/configuration/salary-structures', '/configuration/allowance-types',
    '/configuration/leave-types?is_active=true', '/configuration/public-holidays',
    '/configuration/maternity-leave-policies',
    '/configuration/unpaid-infant-care-leave-relevant-periods',
    '/configuration/cpf-contribution-rates', '/configuration/cpf-pr-year-rates',
    '/configuration/cpf-wage-ceilings', '/configuration/shg-contribution-tiers',
    '/configuration/iras-tax-brackets', '/configuration/iras-tax-reliefs',
    '/configuration/workweek-policies', '/configuration/work-schedule-templates',
    '/configuration/overtime-policies', '/configuration/notice-period-policies',
    '/configuration/retirement-policies', '/configuration/foreign-worker-levies',
    '/configuration/wica-compensations', '/configuration/system-settings',
    '/configuration/integration-keys', '/configuration/custom-fields',
    '/configuration/approval-rules', '/configuration/notification-templates',
    '/configuration/audit-logging-configs',
    '/configuration/safety-certification-types', '/configuration/trade-categories',
    '/configuration/competency-levels', '/configuration/skill-frameworks',
    '/configuration/benefit-plans', '/configuration/insurance-policy-types',
    '/configuration/medical-exam-types', '/configuration/competency-frameworks',
    '/configuration/cpd-requirements', '/configuration/work-pass-quotas',
    '/configuration/spass-salary-requirements', '/configuration/work-permit-requirements',
    '/configuration/construction-worker-upgrade-pathways',
    '/configuration/certification-requirements',
    '/configuration/construction-worker-classifications',
    '/configuration/work-permit-levy-calculations',
    '/configuration/myskillsfuture-courses', '/configuration/course-disciplines',
    '/configuration/training-providers', '/configuration/employee-training-enrollments',
    '/configuration/training-recommendations',
    '/configuration/department-training-budgets', '/configuration/training-applications',
    '/configuration/sponsorship-bond-agreements', '/configuration/training-approval-logs',
    '/configuration/training-booking-tasks', '/configuration/sponsorship-repayments',
    '/configuration/institution-types', '/configuration/education-institutions',
    '/configuration/qualification-levels', '/configuration/fields-of-study',
    '/configuration/certification-authorities', '/configuration/certification-categories',
    '/configuration/certification-templates', '/configuration/mom-working-days-calendars',
  ];

  const results = await Promise.allSettled(endpoints.map(fetchAllPages));
  const val = (r) => (r.status === 'fulfilled' ? r.value || [] : []);
  const [
    departments, locations, classifications, designations, employmentTypes, payGrades,
    salaryStructures, allowanceTypes, leaveTypes, publicHolidays,
    maternityLeavePolicies, unpaidInfantCareLeaveRelevantPeriods,
    cpfRates, cpfPrYearRates, cpfWageCeilings, shgContributionTiers,
    irasTaxBrackets, irasTaxReliefs, workweekPolicies, workScheduleTemplates,
    overtimePolicies, noticePolicies, retirementPolicies, foreignWorkerLevies,
    wicaCompensations, systemSettings, integrationKeys, customFields,
    approvalRules, notificationTemplates, auditConfigs,
    safetyCertifications, tradeCategories, competencyLevels, skillFrameworks,
    benefitPlans, insurancePolicies, medicalExams, competencyFrameworks,
    cpdRequirements, workPassQuotas, spassSalaryRequirements, workPermitRequirements,
    constructionWorkerUpgradePathways, certificationRequirements,
    constructionWorkerClassifications, workPermitLevyCalculations,
    myskillsfutureCourses, courseDisciplines, trainingProviders,
    employeeTrainingEnrollments, trainingRecommendations,
    departmentTrainingBudgets, trainingApplications, sponsorshipBondAgreements,
    trainingApprovalLogs, trainingBookingTasks, sponsorshipRepayments,
    institutionTypes, educationInstitutions, qualificationLevels, fieldsOfStudy,
    certificationAuthorities, certificationCategories, certificationTemplates,
    momWorkingDaysCalendar,
  ] = results;

  return {
    departments: val(departments), locations: val(locations),
    classifications: val(classifications), designations: val(designations),
    employmentTypes: val(employmentTypes), payGrades: val(payGrades),
    salaryStructures: val(salaryStructures), allowanceTypes: val(allowanceTypes),
    leaveTypes: val(leaveTypes), publicHolidays: val(publicHolidays),
    maternityLeavePolicies: val(maternityLeavePolicies),
    unpaidInfantCareLeaveRelevantPeriods: val(unpaidInfantCareLeaveRelevantPeriods),
    cpfRates: val(cpfRates), cpfPrYearRates: val(cpfPrYearRates),
    cpfWageCeilings: val(cpfWageCeilings), shgContributionTiers: val(shgContributionTiers),
    irasTaxBrackets: val(irasTaxBrackets), irasTaxReliefs: val(irasTaxReliefs),
    workweekPolicies: val(workweekPolicies), workScheduleTemplates: val(workScheduleTemplates),
    overtimePolicies: val(overtimePolicies), noticePolicies: val(noticePolicies),
    retirementPolicies: val(retirementPolicies),
    foreignWorkerLevies: val(foreignWorkerLevies),
    wicaCompensations: val(wicaCompensations), systemSettings: val(systemSettings),
    integrationKeys: val(integrationKeys), customFields: val(customFields),
    approvalRules: val(approvalRules), notificationTemplates: val(notificationTemplates),
    auditConfigs: val(auditConfigs), safetyCertifications: val(safetyCertifications),
    tradeCategories: val(tradeCategories), competencyLevels: val(competencyLevels),
    skillFrameworks: val(skillFrameworks), benefitPlans: val(benefitPlans),
    insurancePolicies: val(insurancePolicies), medicalExams: val(medicalExams),
    competencyFrameworks: val(competencyFrameworks), cpdRequirements: val(cpdRequirements),
    workPassQuotas: val(workPassQuotas), spassSalaryRequirements: val(spassSalaryRequirements),
    workPermitRequirements: val(workPermitRequirements),
    constructionWorkerUpgradePathways: val(constructionWorkerUpgradePathways),
    certificationRequirements: val(certificationRequirements),
    constructionWorkerClassifications: val(constructionWorkerClassifications),
    workPermitLevyCalculations: val(workPermitLevyCalculations),
    myskillsfutureCourses: val(myskillsfutureCourses),
    courseDisciplines: val(courseDisciplines), trainingProviders: val(trainingProviders),
    employeeTrainingEnrollments: val(employeeTrainingEnrollments),
    trainingRecommendations: val(trainingRecommendations),
    departmentTrainingBudgets: val(departmentTrainingBudgets),
    trainingApplications: val(trainingApplications),
    sponsorshipBondAgreements: val(sponsorshipBondAgreements),
    trainingApprovalLogs: val(trainingApprovalLogs),
    trainingBookingTasks: val(trainingBookingTasks),
    sponsorshipRepayments: val(sponsorshipRepayments),
    uenVerification: [],
    institutionTypes: val(institutionTypes),
    educationInstitutions: val(educationInstitutions),
    qualificationLevels: val(qualificationLevels), fieldsOfStudy: val(fieldsOfStudy),
    certificationAuthorities: val(certificationAuthorities),
    certificationCategories: val(certificationCategories),
    certificationTemplates: val(certificationTemplates),
    momWorkingDaysCalendar: val(momWorkingDaysCalendar),
  };
};
